import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';
import { useAuth } from '../context/AuthContext';

/* ─── Constants ──────────────────────────────────────────────────────────── */
const STATUSES = ['present','absent','half_day','late','on_leave','holiday','work_from_home'];
const STATUS_META = {
  present:         { label: 'Present',        bg: '#d1fae5', color: '#065f46' },
  absent:          { label: 'Absent',         bg: '#fee2e2', color: '#991b1b' },
  half_day:        { label: 'Half Day',       bg: '#fef3c7', color: '#92400e' },
  late:            { label: 'Late',           bg: '#ffedd5', color: '#c2410c' },
  on_leave:        { label: 'On Leave',       bg: '#ede9fe', color: '#5b21b6' },
  holiday:         { label: 'Holiday',        bg: '#dbeafe', color: '#1e40af' },
  work_from_home:  { label: 'Work From Home', bg: '#f0fdf4', color: '#166534' },
};
const DEPARTMENTS = ['Research & Development','Quality Control','Quality Assurance','Regulatory Affairs',
  'Sales & Marketing','Business Development','Finance','Human Resources','Operations','IT','Administration','Logistics'];

const fmtTime = (t) => {
  if (!t) return '—';
  const [h, m] = t.split(':');
  const hr = parseInt(h), ampm = hr >= 12 ? 'PM' : 'AM';
  return `${hr % 12 || 12}:${m} ${ampm}`;
};
const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const today   = () => new Date().toISOString().split('T')[0];

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, bg: '#f1f5f9', color: '#64748b' };
  return (
    <span style={{ fontSize:'0.72rem', fontWeight:700, padding:'2px 9px', borderRadius:20, background:m.bg, color:m.color, whiteSpace:'nowrap' }}>
      {m.label}
    </span>
  );
}

/* ─── Live Clock ─────────────────────────────────────────────────────────── */
function LiveClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em' }}>
      {time.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', second:'2-digit', hour12:true })}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function Attendance() {
  const { user, isPrivileged } = useAuth();
  const [tab, setTab] = useState('my');

  /* ── My-attendance state ──────────────────────────────────────────────── */
  const [myStatus,  setMyStatus]  = useState(null);   // today's record
  const [myLinked,  setMyLinked]  = useState(true);   // is user linked to an employee
  const [myEmp,     setMyEmp]     = useState(null);
  const [myHistory, setMyHistory] = useState([]);
  const [myTotal,   setMyTotal]   = useState(0);
  const [myMonth,   setMyMonth]   = useState(new Date().getMonth() + 1);
  const [myYear,    setMyYear]    = useState(new Date().getFullYear());
  const [myLoading, setMyLoading] = useState(true);
  const [clockBusy, setClockBusy] = useState(false);
  const [clockError,setClockError]= useState('');

  /* ── Team-attendance state (admin/manager) ────────────────────────────── */
  const [records,    setRecords]    = useState([]);
  const [recTotal,   setRecTotal]   = useState(0);
  const [recPage,    setRecPage]    = useState(1);
  const [recLoading, setRecLoading] = useState(false);
  const [summary,    setSummary]    = useState(null);
  const [employees,  setEmployees]  = useState([]);

  const [filterEmp,   setFilterEmp]  = useState('');
  const [filterDept,  setFilterDept] = useState('');
  const [filterDate,  setFilterDate] = useState(today());
  const [filterStatus,setFilterStatus]= useState('');
  const [filterFrom,  setFilterFrom] = useState('');
  const [filterTo,    setFilterTo]   = useState('');
  const [dateMode,    setDateMode]   = useState('single'); // 'single' | 'range'

  /* ── Bulk upload modal ────────────────────────────────────────────────── */
  const [bulkModal,    setBulkModal]    = useState(false);
  const [bulkRawRows,  setBulkRawRows]  = useState([]);   // original parsed rows (source columns)
  const [bulkMapping,  setBulkMapping]  = useState({});   // { target_field: 'source_col' }
  const [bulkRows,     setBulkRows]     = useState([]);   // mapped rows ready for upload
  const [bulkStep,     setBulkStep]     = useState('upload'); // 'upload' | 'map' | 'preview'
  const [bulkError,    setBulkError]    = useState('');
  const [bulkSaving,   setBulkSaving]   = useState(false);
  const [bulkResult,   setBulkResult]   = useState(null);
  const bulkFileRef = useRef(null);

  const BULK_TARGET_FIELDS = [
    { key:'employee_code', label:'Employee Code', required:true,  aliases:['emp_code','employee_id','emp_id','staff_code','code','empcode','employeecode'] },
    { key:'date',          label:'Date',          required:true,  aliases:['attendance_date','work_date','att_date','day','attendancedate'] },
    { key:'status',        label:'Status',        required:true,  aliases:['attendance_status','attendance','att_status'] },
    { key:'check_in',      label:'Check In',      required:false, aliases:['checkin','in_time','time_in','intime','check in'] },
    { key:'check_out',     label:'Check Out',     required:false, aliases:['checkout','out_time','time_out','outtime','check out'] },
    { key:'notes',         label:'Notes',         required:false, aliases:['note','remarks','remark','comment','comments'] },
  ];

  const norm = s => s.toLowerCase().replace(/[\s_\-]/g, '');

  const autoDetectMapping = (fileColumns) => {
    const mapping = {};
    for (const field of BULK_TARGET_FIELDS) {
      const aliases = [field.key, ...field.aliases].map(norm);
      const match = fileColumns.find(col => aliases.includes(norm(col)));
      mapping[field.key] = match || '';
    }
    return mapping;
  };

  const downloadTemplate = () => {
    const csv = 'employee_code,date,status,check_in,check_out,notes\nEMP001,2026-03-24,present,09:00,18:00,\nEMP002,2026-03-24,absent,,,\nEMP003,2026-03-24,half_day,09:00,13:00,Half day - personal';
    const blob = new Blob([csv], { type:'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'attendance_bulk_template.csv';
    a.click();
  };

  const parseCsv = (text) => {
    const lines = text.trim().split('\n').filter(l => l.trim());
    if (!lines.length) return [];
    const headers = lines[0].split(',').map(h => h.trim());
    return lines.slice(1).map(line => {
      const vals = line.split(',').map(v => v.trim());
      const obj = {};
      headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
      return obj;
    });
  };

  const handleBulkFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBulkError(''); setBulkResult(null); setBulkRows([]);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        let rows;
        if (file.name.endsWith('.json')) {
          rows = JSON.parse(ev.target.result);
          if (!Array.isArray(rows)) throw new Error('JSON must be an array of objects.');
        } else {
          rows = parseCsv(ev.target.result);
        }
        if (!rows.length) { setBulkError('No data rows found.'); return; }
        const raw = rows.slice(0, 1000);
        setBulkRawRows(raw);
        setBulkMapping(autoDetectMapping(Object.keys(raw[0])));
        setBulkStep('map');
      } catch (err) {
        setBulkError('Failed to parse file: ' + err.message);
      }
    };
    reader.readAsText(file);
  };

  const applyMapping = () => {
    const reqMissing = BULK_TARGET_FIELDS.filter(f => f.required && !bulkMapping[f.key]);
    if (reqMissing.length) {
      setBulkError(`Please map required fields: ${reqMissing.map(f => f.label).join(', ')}`);
      return;
    }
    setBulkError('');
    const mapped = bulkRawRows.map(row => {
      const out = {};
      BULK_TARGET_FIELDS.forEach(f => {
        const srcCol = bulkMapping[f.key];
        out[f.key] = srcCol ? (row[srcCol] || '') : '';
      });
      return out;
    });
    setBulkRows(mapped);
    setBulkStep('preview');
  };

  const handleBulkSubmit = async () => {
    if (!bulkRows.length) return;
    setBulkSaving(true); setBulkError(''); setBulkResult(null);
    try {
      const { data } = await api.post('/attendance/bulk', bulkRows);
      setBulkResult(data.data);
      setBulkStep('done');
      loadRecords(); loadSummary();
      if (bulkFileRef.current) bulkFileRef.current.value = '';
    } catch (err) {
      setBulkError(err.response?.data?.message || 'Bulk upload failed.');
    } finally { setBulkSaving(false); }
  };

  const resetBulk = () => {
    setBulkRawRows([]); setBulkRows([]); setBulkMapping({});
    setBulkStep('upload'); setBulkError(''); setBulkResult(null);
    if (bulkFileRef.current) bulkFileRef.current.value = '';
  };

  /* ── Edit / Add modal ─────────────────────────────────────────────────── */
  const [editModal,  setEditModal]  = useState(false);
  const [editRecord, setEditRecord] = useState(null);  // null = new
  const [freezeConfirm, setFreezeConfirm] = useState(null);
  const [editForm,   setEditForm]   = useState({ employee_id:'', date: today(), check_in:'', check_out:'', status:'present', notes:'' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError,  setEditError]  = useState('');

  /* ─── Load my status & history ─────────────────────────────────────────── */
  const loadMyStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/attendance/my-status');
      setMyLinked(data.linked);
      setMyStatus(data.data);
      setMyEmp(data.employee);
    } catch {}
  }, []);

  const loadMyHistory = useCallback(async () => {
    setMyLoading(true);
    try {
      const { data } = await api.get('/attendance/my', { params: { month: myMonth, year: myYear, limit: 35 } });
      setMyHistory(data.data || []);
      setMyTotal(data.total || 0);
      if (!myLinked && data.linked !== undefined) setMyLinked(data.linked);
    } catch {}
    finally { setMyLoading(false); }
  }, [myMonth, myYear, myLinked]);

  useEffect(() => { loadMyStatus(); }, [loadMyStatus]);
  useEffect(() => { if (tab === 'my') loadMyHistory(); }, [tab, loadMyHistory]);

  /* ─── Load team records ─────────────────────────────────────────────────── */
  const loadRecords = useCallback(async () => {
    if (!isPrivileged) return;
    setRecLoading(true);
    try {
      const params = { page: recPage, limit: 50, status: filterStatus || undefined };
      if (filterEmp)  params.employee_id = filterEmp;
      if (filterDept) params.department  = filterDept;
      if (dateMode === 'single' && filterDate) params.date = filterDate;
      if (dateMode === 'range') {
        if (filterFrom) params.from_date = filterFrom;
        if (filterTo)   params.to_date   = filterTo;
      }
      const { data } = await api.get('/attendance', { params });
      setRecords(data.data || []);
      setRecTotal(data.total || 0);
    } catch {}
    finally { setRecLoading(false); }
  }, [isPrivileged, recPage, filterEmp, filterDept, filterDate, filterFrom, filterTo, filterStatus, dateMode]);

  const loadSummary = useCallback(async () => {
    if (!isPrivileged) return;
    try {
      const date = dateMode === 'single' ? filterDate : today();
      const { data } = await api.get('/attendance/summary', { params: { date } });
      setSummary(data.data);
    } catch {}
  }, [isPrivileged, filterDate, dateMode]);

  const loadEmployees = useCallback(async () => {
    if (!isPrivileged || employees.length) return;
    try {
      const { data } = await api.get('/hr', { params: { limit: 300 } });
      setEmployees(data.data || []);
    } catch {}
  }, [isPrivileged, employees.length]);

  useEffect(() => {
    if (tab === 'team') {
      loadRecords();
      loadSummary();
      loadEmployees();
    }
  }, [tab, loadRecords, loadSummary, loadEmployees]);

  /* ─── Clock In / Out ────────────────────────────────────────────────────── */
  const handleClockIn = async () => {
    setClockBusy(true); setClockError('');
    try {
      const { data } = await api.post('/attendance/checkin');
      setMyStatus(data.data);
    } catch (e) { setClockError(e.response?.data?.message || 'Failed to check in.'); }
    finally { setClockBusy(false); }
  };

  const handleClockOut = async () => {
    setClockBusy(true); setClockError('');
    try {
      const { data } = await api.post('/attendance/checkout');
      setMyStatus(data.data);
      loadMyHistory();
    } catch (e) { setClockError(e.response?.data?.message || 'Failed to check out.'); }
    finally { setClockBusy(false); }
  };

  /* ─── Edit / Add modal handlers ─────────────────────────────────────────── */
  const openAdd = () => {
    setEditRecord(null);
    setEditForm({ employee_id:'', date: dateMode === 'single' ? filterDate : today(), check_in:'', check_out:'', status:'present', notes:'' });
    setEditError(''); setEditModal(true);
  };

  const openEdit = (rec) => {
    setEditRecord(rec);
    setEditForm({
      employee_id: rec.employee_id,
      date:        rec.date?.split('T')[0] || rec.date,
      check_in:    rec.check_in  ? rec.check_in.slice(0,5)  : '',
      check_out:   rec.check_out ? rec.check_out.slice(0,5) : '',
      status:      rec.status || 'present',
      notes:       rec.notes || '',
    });
    setEditError(''); setEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editForm.employee_id && !editRecord) { setEditError('Please select an employee.'); return; }
    setEditSaving(true); setEditError('');
    try {
      if (editRecord) {
        await api.put(`/attendance/${editRecord.id}`, editForm);
      } else {
        await api.post('/attendance', editForm);
      }
      setEditModal(false);
      loadRecords();
      loadSummary();
    } catch (e) { setEditError(e.response?.data?.message || 'Failed to save.'); }
    finally { setEditSaving(false); }
  };

  const handleFreeze = async () => {
    try {
      await api.delete(`/attendance/${freezeConfirm.id}`);
      setFreezeConfirm(null);
      loadRecords(); loadSummary();
    } catch (e) { alert(e.response?.data?.message || 'Failed to freeze.'); }
  };

  /* ─── Compute my-month summary ───────────────────────────────────────────── */
  const mySummary = myHistory.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div className="page">
      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Attendance</h1>
          <p className="page-subtitle">Track daily attendance — check in, check out, and review records</p>
        </div>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <div style={{ display:'flex', gap:4, marginBottom:20, borderBottom:'2px solid var(--border)', paddingBottom:0 }}>
        {[{ key:'my', label:'My Attendance' }, ...(isPrivileged ? [{ key:'team', label:'Team Attendance' }] : [])].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding:'8px 18px', border:'none', background:'none', cursor:'pointer',
            fontWeight: tab === t.key ? 700 : 500, fontSize:'0.875rem',
            color: tab === t.key ? 'var(--primary)' : 'var(--text-secondary)',
            borderBottom: tab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
            marginBottom: -2, transition:'all 0.15s'
          }}>{t.label}</button>
        ))}
      </div>

      {/* ══════════════════════ MY ATTENDANCE TAB ═══════════════════════════ */}
      {tab === 'my' && (
        <div>
          {!myLinked && (
            <div style={{ background:'#fef3c7', border:'1px solid #f59e0b', borderRadius:10, padding:'14px 18px', marginBottom:20, color:'#92400e', fontSize:'0.875rem' }}>
              <strong>No employee profile linked.</strong> Ask your admin to link your user account to an employee record in HR to enable attendance tracking.
            </div>
          )}

          {myLinked && (
            <>
              {/* ── Clock widget ────────────────────────────────────────── */}
              <div style={{ display:'flex', gap:16, flexWrap:'wrap', marginBottom:20 }}>
                <div style={{ flex:'1 1 320px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'22px 24px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
                    <div>
                      <div style={{ fontSize:'0.75rem', fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>
                        {new Date().toLocaleDateString('en-IN', { weekday:'long', day:'2-digit', month:'long', year:'numeric' })}
                      </div>
                      <div style={{ fontSize:'2rem', fontWeight:800, color:'var(--text-primary)', lineHeight:1.1 }}>
                        <LiveClock />
                      </div>
                      {myEmp && (
                        <div style={{ fontSize:'0.8rem', color:'var(--text-secondary)', marginTop:6 }}>
                          {myEmp.first_name} {myEmp.last_name || ''} · {myEmp.employee_code} · {myEmp.department || ''}
                        </div>
                      )}
                    </div>

                    <div style={{ display:'flex', flexDirection:'column', gap:8, minWidth:140, alignItems:'flex-end' }}>
                      {myStatus?.check_in ? (
                        <>
                          <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', textAlign:'right' }}>
                            Checked in at <strong style={{ color:'#065f46' }}>{fmtTime(myStatus.check_in)}</strong>
                          </div>
                          {myStatus.check_out ? (
                            <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', textAlign:'right' }}>
                              Checked out at <strong style={{ color:'#1e40af' }}>{fmtTime(myStatus.check_out)}</strong>
                              <br/>Hours: <strong>{myStatus.working_hours}h</strong>
                            </div>
                          ) : (
                            <button className="btn btn-primary btn-sm" onClick={handleClockOut} disabled={clockBusy}
                              style={{ background:'#1e40af', minWidth:120 }}>
                              {clockBusy ? 'Please wait…' : '⬆ Check Out'}
                            </button>
                          )}
                        </>
                      ) : (
                        <button className="btn btn-primary btn-sm" onClick={handleClockIn} disabled={clockBusy}
                          style={{ minWidth:120, background:'#065f46' }}>
                          {clockBusy ? 'Please wait…' : '⬇ Check In'}
                        </button>
                      )}
                      {myStatus && <StatusBadge status={myStatus.status} />}
                    </div>
                  </div>
                  {clockError && (
                    <div style={{ marginTop:10, color:'var(--error)', fontSize:'0.8rem' }}>{clockError}</div>
                  )}
                </div>

                {/* ── Month summary cards ───────────────────────────────── */}
                <div style={{ display:'flex', gap:10, flexWrap:'wrap', flex:'1 1 320px' }}>
                  {[
                    { key:'present',        label:'Present',   color:'#065f46', bg:'#d1fae5' },
                    { key:'absent',         label:'Absent',    color:'#991b1b', bg:'#fee2e2' },
                    { key:'half_day',       label:'Half Day',  color:'#92400e', bg:'#fef3c7' },
                    { key:'work_from_home', label:'WFH',       color:'#166534', bg:'#f0fdf4' },
                  ].map(s => (
                    <div key={s.key} style={{ flex:'1 1 80px', background:s.bg, border:`1px solid ${s.bg}`, borderRadius:10, padding:'14px 12px', textAlign:'center', minWidth:80 }}>
                      <div style={{ fontSize:'1.6rem', fontWeight:800, color:s.color }}>{mySummary[s.key] || 0}</div>
                      <div style={{ fontSize:'0.68rem', fontWeight:600, color:s.color, textTransform:'uppercase', letterSpacing:'0.05em', marginTop:2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Month picker + history table ─────────────────────────── */}
              <div className="card">
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px', borderBottom:'1px solid var(--border)' }}>
                  <span style={{ fontWeight:700, fontSize:'0.9rem' }}>Attendance History</span>
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <select className="form-control form-select" style={{ width:100 }} value={myMonth}
                      onChange={e => setMyMonth(Number(e.target.value))}>
                      {MONTHS.map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
                    </select>
                    <select className="form-control form-select" style={{ width:90 }} value={myYear}
                      onChange={e => setMyYear(Number(e.target.value))}>
                      {[2023,2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </div>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Day</th>
                        <th>Check In</th>
                        <th>Check Out</th>
                        <th>Hours</th>
                        <th>Status</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {myLoading && <tr><td colSpan={7}><div className="loading-overlay"><div className="spinner"/></div></td></tr>}
                      {!myLoading && myHistory.length === 0 && (
                        <tr><td colSpan={7}><div className="empty-state"><p>No records for {MONTHS[myMonth-1]} {myYear}.</p></div></td></tr>
                      )}
                      {!myLoading && myHistory.map(r => {
                        const d = new Date(r.date + 'T00:00:00');
                        return (
                          <tr key={r.id}>
                            <td style={{ fontWeight:600 }}>{fmtDate(r.date)}</td>
                            <td style={{ color:'var(--text-muted)', fontSize:'0.8rem' }}>{d.toLocaleDateString('en-IN',{weekday:'short'})}</td>
                            <td style={{ color:'#065f46', fontWeight:500 }}>{fmtTime(r.check_in)}</td>
                            <td style={{ color:'#1e40af', fontWeight:500 }}>{fmtTime(r.check_out)}</td>
                            <td>{r.working_hours > 0 ? `${r.working_hours}h` : '—'}</td>
                            <td><StatusBadge status={r.status}/></td>
                            <td style={{ fontSize:'0.78rem', color:'var(--text-muted)' }}>{r.notes || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ══════════════════════ TEAM ATTENDANCE TAB ══════════════════════════ */}
      {tab === 'team' && isPrivileged && (
        <div>
          {/* ── Daily summary cards ─────────────────────────────────────── */}
          {summary && (
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:16 }}>
              {[
                { key:'present',        label:'Present',     color:'#065f46', bg:'#d1fae5' },
                { key:'absent',         label:'Absent',      color:'#991b1b', bg:'#fee2e2' },
                { key:'half_day',       label:'Half Day',    color:'#92400e', bg:'#fef3c7' },
                { key:'late',           label:'Late',        color:'#c2410c', bg:'#ffedd5' },
                { key:'on_leave',       label:'On Leave',    color:'#5b21b6', bg:'#ede9fe' },
                { key:'work_from_home', label:'WFH',         color:'#166534', bg:'#f0fdf4' },
                { key:'not_marked',     label:'Not Marked',  color:'#475569', bg:'#f1f5f9' },
              ].map(s => (
                <div key={s.key} style={{ flex:'1 1 90px', background:s.bg, borderRadius:10, padding:'12px 10px', textAlign:'center', minWidth:90 }}>
                  <div style={{ fontSize:'1.5rem', fontWeight:800, color:s.color }}>{summary[s.key] || 0}</div>
                  <div style={{ fontSize:'0.65rem', fontWeight:700, color:s.color, textTransform:'uppercase', letterSpacing:'0.05em', marginTop:2 }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* ── Filters ─────────────────────────────────────────────────── */}
          <div className="card" style={{ marginBottom:14 }}>
            <div className="card-body" style={{ padding:'12px 14px' }}>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                {/* Date mode toggle */}
                <div style={{ display:'flex', gap:0, borderRadius:8, overflow:'hidden', border:'1px solid var(--border)' }}>
                  {['single','range'].map(m => (
                    <button key={m} onClick={() => setDateMode(m)} style={{
                      padding:'6px 12px', border:'none', cursor:'pointer', fontSize:'0.8rem', fontWeight:600,
                      background: dateMode === m ? 'var(--primary)' : 'var(--surface)', color: dateMode === m ? '#fff' : 'var(--text-secondary)',
                    }}>{m === 'single' ? 'Date' : 'Range'}</button>
                  ))}
                </div>

                {dateMode === 'single' ? (
                  <input type="date" className="form-control" value={filterDate} style={{ width:150 }}
                    onChange={e => { setFilterDate(e.target.value); setRecPage(1); }} />
                ) : (
                  <>
                    <input type="date" className="form-control" value={filterFrom} placeholder="From" style={{ width:140 }}
                      onChange={e => { setFilterFrom(e.target.value); setRecPage(1); }} />
                    <span style={{ color:'var(--text-muted)', fontSize:'0.8rem' }}>to</span>
                    <input type="date" className="form-control" value={filterTo} placeholder="To" style={{ width:140 }}
                      onChange={e => { setFilterTo(e.target.value); setRecPage(1); }} />
                  </>
                )}

                <select className="form-control form-select" style={{ width:190 }} value={filterEmp}
                  onChange={e => { setFilterEmp(e.target.value); setRecPage(1); }}>
                  <option value="">All Employees</option>
                  {employees.map(e => (
                    <option key={e.id} value={e.id}>{e.first_name} {e.last_name || ''} ({e.employee_code})</option>
                  ))}
                </select>

                <select className="form-control form-select" style={{ width:160 }} value={filterDept}
                  onChange={e => { setFilterDept(e.target.value); setRecPage(1); }}>
                  <option value="">All Departments</option>
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>

                <select className="form-control form-select" style={{ width:140 }} value={filterStatus}
                  onChange={e => { setFilterStatus(e.target.value); setRecPage(1); }}>
                  <option value="">All Status</option>
                  {STATUSES.map(s => <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>)}
                </select>

                <button className="btn btn-primary btn-sm" onClick={() => { loadRecords(); loadSummary(); }}>Apply</button>
                <button className="btn btn-secondary btn-sm" onClick={openAdd}>+ Add Record</button>
                <button className="btn btn-secondary btn-sm" onClick={() => { setBulkModal(true); setBulkRows([]); setBulkError(''); setBulkResult(null); }}
                  style={{ display:'flex', alignItems:'center', gap:5 }}>
                  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  Bulk Upload
                </button>

                {(filterEmp || filterDept || filterStatus) && (
                  <button className="btn btn-ghost btn-sm" onClick={() => { setFilterEmp(''); setFilterDept(''); setFilterStatus(''); setRecPage(1); }}>Clear</button>
                )}
              </div>
            </div>
          </div>

          {/* ── Records table ───────────────────────────────────────────── */}
          <div className="card">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Department</th>
                    <th>Date</th>
                    <th>Check In</th>
                    <th>Check Out</th>
                    <th>Hours</th>
                    <th>Status</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recLoading && <tr><td colSpan={9}><div className="loading-overlay"><div className="spinner"/></div></td></tr>}
                  {!recLoading && records.length === 0 && (
                    <tr><td colSpan={9}><div className="empty-state"><p>No attendance records for the selected filters.</p></div></td></tr>
                  )}
                  {!recLoading && records.map(r => (
                    <tr key={r.id}>
                      <td>
                        <div style={{ fontWeight:600 }}>{r.employee_name}</div>
                        <div style={{ fontSize:'0.73rem', color:'var(--text-muted)' }}>{r.employee_code}</div>
                      </td>
                      <td style={{ fontSize:'0.8rem', color:'var(--text-secondary)' }}>{r.department || '—'}</td>
                      <td style={{ fontWeight:500, fontSize:'0.83rem' }}>{fmtDate(r.date)}</td>
                      <td style={{ color:'#065f46', fontWeight:500 }}>{fmtTime(r.check_in)}</td>
                      <td style={{ color:'#1e40af', fontWeight:500 }}>{fmtTime(r.check_out)}</td>
                      <td>{r.working_hours > 0 ? `${r.working_hours}h` : '—'}</td>
                      <td><StatusBadge status={r.status}/></td>
                      <td style={{ fontSize:'0.78rem', color:'var(--text-muted)', maxWidth:120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.notes || '—'}</td>
                      <td>
                        <div className="table-actions">
                          <button className="btn btn-ghost btn-icon" data-tooltip="Edit" onClick={() => openEdit(r)}>
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          <button className="btn btn-ghost btn-icon" data-tooltip="Freeze" style={{ color:'var(--error)' }} onClick={() => setFreezeConfirm(r)}>
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {recTotal > 50 && (
              <div style={{ display:'flex', justifyContent:'flex-end', padding:'10px 16px', gap:8, fontSize:'0.8rem', color:'var(--text-muted)' }}>
                Showing {Math.min(50, recTotal)} of {recTotal} records.
                {recPage > 1 && <button className="btn btn-ghost btn-sm" onClick={() => setRecPage(p => p - 1)}>← Prev</button>}
                <button className="btn btn-ghost btn-sm" onClick={() => setRecPage(p => p + 1)}>Next →</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════ EDIT / ADD MODAL ═════════════════════════════ */}
      <Modal open={editModal} onClose={() => setEditModal(false)}
        title={editRecord ? 'Edit Attendance Record' : 'Add Attendance Record'} size="md"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setEditModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleEditSave} disabled={editSaving}>
            {editSaving ? 'Saving…' : 'Save'}
          </button>
        </>}
      >
        {editError && <div className="alert alert-error" style={{ marginBottom:12 }}>{editError}</div>}

        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {!editRecord && (
            <div className="form-group">
              <label className="form-label">Employee *</label>
              <select className="form-control form-select" value={editForm.employee_id}
                onChange={e => setEditForm(p => ({ ...p, employee_id: e.target.value }))}>
                <option value="">Select employee</option>
                {employees.map(e => (
                  <option key={e.id} value={e.id}>{e.first_name} {e.last_name || ''} ({e.employee_code})</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date *</label>
              <input className="form-control" type="date" value={editForm.date}
                onChange={e => setEditForm(p => ({ ...p, date: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-control form-select" value={editForm.status}
                onChange={e => setEditForm(p => ({ ...p, status: e.target.value }))}>
                {STATUSES.map(s => <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Check In</label>
              <input className="form-control" type="time" value={editForm.check_in}
                onChange={e => setEditForm(p => ({ ...p, check_in: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Check Out</label>
              <input className="form-control" type="time" value={editForm.check_out}
                onChange={e => setEditForm(p => ({ ...p, check_out: e.target.value }))} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <input className="form-control" placeholder="e.g. WFH due to travel, late due to client meeting…"
              value={editForm.notes} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} />
          </div>
        </div>
      </Modal>

      {/* ══════════════════════ BULK UPLOAD MODAL ════════════════════════════ */}
      <Modal open={bulkModal} onClose={() => { setBulkModal(false); resetBulk(); }} title="Bulk Upload Attendance" size="lg">
        <div style={{ display:'grid', gap:18 }}>

          {/* ── Step indicator ── */}
          <div style={{ display:'flex', alignItems:'center', gap:0 }}>
            {[
              { key:'upload',  num:1, label:'Upload File' },
              { key:'map',     num:2, label:'Map Fields'  },
              { key:'preview', num:3, label:'Preview'     },
              { key:'done',    num:4, label:'Done'        },
            ].map((s, i, arr) => {
              const steps = ['upload','map','preview','done'];
              const cur = steps.indexOf(bulkStep);
              const idx = steps.indexOf(s.key);
              const active = idx === cur;
              const done   = idx < cur;
              return (
                <React.Fragment key={s.key}>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
                    <div style={{
                      width:28, height:28, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
                      fontWeight:700, fontSize:'0.78rem',
                      background: done ? '#1d4ed8' : active ? '#1d4ed8' : 'var(--surface-2)',
                      color: (done || active) ? '#fff' : 'var(--text-muted)',
                      border: active ? '2px solid #1d4ed8' : done ? '2px solid #1d4ed8' : '2px solid var(--border-dark)',
                    }}>
                      {done ? '✓' : s.num}
                    </div>
                    <span style={{ fontSize:'0.65rem', fontWeight:active?700:400, color: active?'var(--primary)':'var(--text-muted)', whiteSpace:'nowrap' }}>{s.label}</span>
                  </div>
                  {i < arr.length - 1 && (
                    <div style={{ flex:1, height:2, background: done ? '#1d4ed8' : 'var(--border)', margin:'0 6px', marginBottom:18 }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {bulkError && (
            <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:'var(--radius-sm)', padding:'10px 14px', color:'#dc2626', fontSize:'0.82rem', display:'flex', alignItems:'center', gap:8 }}>
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {bulkError}
            </div>
          )}

          {/* ── STEP 1: Upload ── */}
          {bulkStep === 'upload' && (
            <>
              <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:'var(--radius)', padding:'14px 16px' }}>
                <div style={{ fontWeight:600, fontSize:'0.83rem', color:'#1e40af', marginBottom:6 }}>Instructions</div>
                <ul style={{ margin:0, paddingLeft:18, fontSize:'0.8rem', color:'#1e40af', lineHeight:1.9 }}>
                  <li>Upload a <strong>CSV</strong> or <strong>JSON</strong> file — column names can be anything, you'll map them next.</li>
                  <li>Required fields: <strong>Employee Code</strong>, <strong>Date</strong> (YYYY-MM-DD), <strong>Status</strong></li>
                  <li>Optional: <strong>Check In</strong> (HH:MM), <strong>Check Out</strong> (HH:MM), <strong>Notes</strong></li>
                  <li>Valid statuses: present, absent, half_day, late, on_leave, holiday, work_from_home</li>
                  <li>Existing records for same employee + date will be <strong>overwritten</strong>.</li>
                </ul>
                <button className="btn btn-secondary btn-sm" onClick={downloadTemplate}
                  style={{ marginTop:10, display:'inline-flex', alignItems:'center', gap:5 }}>
                  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  Download CSV Template
                </button>
              </div>
              <div>
                <label className="form-label">Select File (CSV or JSON)</label>
                <input ref={bulkFileRef} type="file" accept=".csv,.json" className="form-control"
                  onChange={handleBulkFile} style={{ padding:'7px 10px', cursor:'pointer' }} />
              </div>
            </>
          )}

          {/* ── STEP 2: Map Fields ── */}
          {bulkStep === 'map' && bulkRawRows.length > 0 && (
            <>
              <div style={{ fontSize:'0.82rem', color:'var(--text-secondary)' }}>
                <strong>{bulkRawRows.length} rows</strong> detected. Map your file's columns to the expected fields.
                Auto-detected matches are pre-filled — adjust as needed.
              </div>

              <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius)', overflow:'hidden' }}>
                {/* Header */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 40px 1fr', gap:0, background:'var(--surface-2)', padding:'8px 14px', borderBottom:'1px solid var(--border)' }}>
                  <span style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase' }}>Expected Field</span>
                  <span />
                  <span style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase' }}>Your File Column</span>
                </div>
                {BULK_TARGET_FIELDS.map((field, i) => {
                  const fileColumns = Object.keys(bulkRawRows[0]);
                  const val = bulkMapping[field.key] || '';
                  const matched = !!val;
                  return (
                    <div key={field.key} style={{
                      display:'grid', gridTemplateColumns:'1fr 40px 1fr', gap:0, alignItems:'center',
                      padding:'10px 14px',
                      borderBottom: i < BULK_TARGET_FIELDS.length - 1 ? '1px solid var(--border)' : 'none',
                      background: matched ? '#fff' : '#fffbeb',
                    }}>
                      {/* Left: target field */}
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontWeight:600, fontSize:'0.83rem', color:'var(--text-primary)' }}>{field.label}</span>
                        {field.required && <span style={{ fontSize:'0.65rem', background:'#fee2e2', color:'#dc2626', borderRadius:4, padding:'1px 5px', fontWeight:700 }}>required</span>}
                      </div>
                      {/* Arrow */}
                      <div style={{ textAlign:'center', color: matched ? '#1d4ed8' : 'var(--text-muted)', fontSize:'1rem' }}>
                        {matched ? '→' : '?'}
                      </div>
                      {/* Right: dropdown */}
                      <select
                        className="form-control"
                        value={val}
                        onChange={e => setBulkMapping(m => ({ ...m, [field.key]: e.target.value }))}
                        style={{ fontSize:'0.82rem', borderColor: !matched && field.required ? '#fca5a5' : undefined }}
                      >
                        <option value="">— Not mapped —</option>
                        {fileColumns.map(col => (
                          <option key={col} value={col}>{col}
                            {bulkRawRows[0][col] ? ` (e.g. "${bulkRawRows[0][col]}")` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>

              {/* Sample preview of first raw row */}
              <div style={{ background:'var(--surface-2)', borderRadius:'var(--radius-sm)', padding:'10px 14px' }}>
                <div style={{ fontSize:'0.7rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', marginBottom:6 }}>First row in your file</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                  {Object.entries(bulkRawRows[0]).map(([k, v]) => (
                    <div key={k} style={{ fontSize:'0.75rem', background:'#fff', border:'1px solid var(--border)', borderRadius:6, padding:'3px 8px' }}>
                      <span style={{ color:'var(--text-muted)' }}>{k}: </span>
                      <span style={{ fontWeight:600, color:'var(--text-primary)' }}>{v || '—'}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── STEP 3: Preview ── */}
          {bulkStep === 'preview' && bulkRows.length > 0 && (
            <div>
              <div style={{ fontSize:'0.82rem', color:'var(--text-secondary)', marginBottom:8 }}>
                <strong>{bulkRows.length} records</strong> ready to upload. Showing first 20.
              </div>
              <div style={{ maxHeight:260, overflowY:'auto', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)' }}>
                <table className="table" style={{ fontSize:'0.78rem', margin:0 }}>
                  <thead>
                    <tr>
                      {BULK_TARGET_FIELDS.map(f => (
                        <th key={f.key} style={{ padding:'6px 10px', fontSize:'0.7rem' }}>{f.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bulkRows.slice(0, 20).map((row, i) => (
                      <tr key={i}>
                        {BULK_TARGET_FIELDS.map(f => (
                          <td key={f.key} style={{ padding:'5px 10px', maxWidth:130, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {f.key === 'status' && row[f.key]
                              ? <StatusBadge status={row[f.key]} />
                              : row[f.key] || <span style={{ color:'var(--text-muted)' }}>—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {bulkRows.length > 20 && (
                  <div style={{ padding:'6px 12px', fontSize:'0.73rem', color:'var(--text-muted)', textAlign:'center', borderTop:'1px solid var(--border)' }}>
                    … and {bulkRows.length - 20} more rows
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── STEP 4: Done ── */}
          {bulkStep === 'done' && bulkResult && (
            <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:'var(--radius)', padding:'18px 20px' }}>
              <div style={{ fontWeight:700, fontSize:'0.9rem', color:'#15803d', marginBottom:12 }}>Upload Complete</div>
              <div style={{ display:'flex', gap:28, flexWrap:'wrap', marginBottom: bulkResult.errors?.length ? 14 : 0 }}>
                {[
                  { label:'Inserted', value: bulkResult.inserted, color:'#16a34a' },
                  { label:'Updated',  value: bulkResult.updated,  color:'#2563eb' },
                  { label:'Errors',   value: bulkResult.errors?.length || 0, color:'#dc2626' },
                ].map(s => (
                  <div key={s.label} style={{ textAlign:'center' }}>
                    <div style={{ fontSize:'1.6rem', fontWeight:800, color:s.color }}>{s.value}</div>
                    <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase' }}>{s.label}</div>
                  </div>
                ))}
              </div>
              {bulkResult.errors?.length > 0 && (
                <div style={{ maxHeight:140, overflowY:'auto', background:'#fff', borderRadius:6, border:'1px solid #fecaca', padding:'8px 12px' }}>
                  {bulkResult.errors.map((e, i) => (
                    <div key={i} style={{ fontSize:'0.75rem', color:'#dc2626', padding:'3px 0', borderBottom: i < bulkResult.errors.length-1 ? '1px solid #fef2f2' : 'none' }}>
                      Row {e.row}: {e.reason}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Footer actions ── */}
          <div style={{ display:'flex', gap:10, justifyContent:'space-between', alignItems:'center' }}>
            <div>
              {bulkStep !== 'upload' && bulkStep !== 'done' && (
                <button className="btn btn-ghost btn-sm" onClick={() => { setBulkStep(bulkStep === 'preview' ? 'map' : 'upload'); setBulkError(''); }}>
                  ← Back
                </button>
              )}
            </div>
            <div style={{ display:'flex', gap:10 }}>
              {bulkStep === 'done'
                ? <><button className="btn btn-secondary" onClick={resetBulk}>Upload Another</button>
                    <button className="btn btn-primary" onClick={() => { setBulkModal(false); resetBulk(); }}>Done</button></>
                : <><button className="btn btn-secondary" onClick={() => { setBulkModal(false); resetBulk(); }}>Cancel</button>
                    {bulkStep === 'map' && (
                      <button className="btn btn-primary" onClick={applyMapping}>Preview →</button>
                    )}
                    {bulkStep === 'preview' && (
                      <button className="btn btn-primary" onClick={handleBulkSubmit} disabled={bulkSaving} style={{ minWidth:150 }}>
                        {bulkSaving
                          ? <span style={{ display:'flex', alignItems:'center', gap:7 }}><span className="spinner" style={{ width:14, height:14, borderWidth:2 }} /> Uploading…</span>
                          : `Upload ${bulkRows.length} Record${bulkRows.length !== 1 ? 's' : ''}`}
                      </button>
                    )}
                  </>
              }
            </div>
          </div>
        </div>
      </Modal>

      <Modal open={!!freezeConfirm} onClose={() => setFreezeConfirm(null)} title="Freeze Attendance Record"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setFreezeConfirm(null)}>Cancel</button>
          <button className="btn btn-danger" onClick={handleFreeze}>Freeze</button>
        </>}
      >
        <p>Freeze attendance record for <strong>{freezeConfirm?.employee_name}</strong> on <strong>{freezeConfirm?.date}</strong>? It will become read-only.</p>
      </Modal>
    </div>
  );
}
